import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/coastal-dusk.css';

const PrivacyNoticePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasPaidAccess, setHasPaidAccess] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setHasPaidAccess(false);
          return;
        }
        const response = await axios.get(
          `${BACKEND_URL}/api/interpretation/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHasPaidAccess(response.data.has_paid_access);
      } catch (err) {
        console.error('Error checking access:', err);
        setHasPaidAccess(false);
      }
    };
    checkAccess();
  }, [BACKEND_URL]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/auth/record-consent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let currentPaidAccess = hasPaidAccess;
      try {
        const statusResponse = await axios.get(
          `${BACKEND_URL}/api/interpretation/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentPaidAccess = statusResponse.data.has_paid_access;
      } catch (statusErr) {
        console.error('Error re-checking status:', statusErr);
      }
      if (currentPaidAccess) {
        navigate('/settling-in');
      } else {
        navigate('/payment');
      }
    } catch (err) {
      console.error('Error recording consent:', err);
      try {
        const token = localStorage.getItem('token');
        const statusResponse = await axios.get(
          `${BACKEND_URL}/api/interpretation/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (statusResponse.data.has_paid_access) {
          navigate('/settling-in');
        } else {
          navigate('/payment');
        }
      } catch (e) {
        navigate('/payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const sectionStyle = {
    marginBottom: '32px',
  };

  const headingStyle = {
    fontFamily: 'var(--font-body)',
    fontWeight: 400,
    fontSize: '0.82rem',
    letterSpacing: '0.04em',
    color: 'var(--accent-teal)',
    marginBottom: '12px',
  };

  const bodyStyle = {
    fontFamily: 'var(--font-body)',
    fontWeight: 300,
    fontSize: '0.78rem',
    lineHeight: '1.75',
    color: 'rgba(163, 163, 163, 0.75)',
  };

  const bulletStyle = {
    ...bodyStyle,
    paddingLeft: '12px',
    marginBottom: '6px',
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />

      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.4', top: '-5%', right: '-10%', width: '45vw', height: '45vw', maxWidth: '500px', maxHeight: '500px' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.3', top: '50%', left: '-8%', width: '40vw', height: '40vw', maxWidth: '450px', maxHeight: '450px', animationDelay: '-8s' }} />

      {/* Content */}
      <div className="py-16 px-6 relative z-10">
        <div className="max-w-xl mx-auto">
          {/* Header removed — page starts directly with content */}

          {/* Full-page scrollable content */}
          <div style={{ padding: '36px 28px' }}>

            {/* DISCLOSURE */}
            <div style={sectionStyle}>
              <h2 style={headingStyle}>Disclosure</h2>
              <div style={bodyStyle}>
                <p style={{ marginBottom: '8px' }}>Clarity Soundtrack offers guided reflective prompts and AI-generated insights to support personal clarity. These insights are suggestions — not directives.</p>
                <p style={{ marginBottom: '8px' }}>Clarity Soundtrack does not provide:</p>
                <p style={bulletStyle}>- Financial, investment, or tax advice</p>
                <p style={bulletStyle}>- Medical advice or mental health treatment</p>
                <p style={bulletStyle}>- Legal or professional recommendations of any kind</p>
                <p style={{ marginTop: '8px' }}>All insights are AI-generated. No human reviews your journal entries or your insights at any point. Your experience is personal and self-directed. We cannot guarantee any specific emotional, financial, or personal outcome. How you choose to interpret or act on your reflections remains entirely your responsibility.</p>
              </div>
            </div>

            {/* REFUND POLICY */}
            <div style={sectionStyle}>
              <h2 style={headingStyle}>Refund Policy</h2>
              <p style={bodyStyle}>This journaling experience is delivered and made accessible instantly. Because the value cannot be returned or revoked, all sales are final and refunds are not applicable.</p>
            </div>

            {/* PRIVACY POLICY */}
            <div style={sectionStyle}>
              <h2 style={headingStyle}>Privacy Policy</h2>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8, marginTop: '12px' }}>How Your Data Is Handled</h3>
              <p style={bodyStyle}>We follow a data-minimization philosophy. Here is the complete picture of how your information is treated:</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Journal Entries</h3>
              <p style={bodyStyle}>Your reflections are used solely to generate your AI-powered insights, then permanently deleted. They are never stored long-term, never reviewed by a human, and cannot be retrieved or restored after deletion.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Your Insights</h3>
              <p style={bodyStyle}>The insights generated from your session belong only to you. You may download or revisit them at any time. If you prefer not to retain your insights — for example, if you choose not to create a login — simply let us know after your session ends. Insights will be deleted within 24 hours and cannot be restored after that point.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>What We Collect</h3>
              <div style={bodyStyle}>
                <p style={bulletStyle}>- <em>Information you provide:</em> Journal entries (temporary), email address, payment information (processed by third-party providers — we do not store card details)</p>
                <p style={bulletStyle}>- <em>Automatically collected:</em> Device type, browser type, general usage data, IP address (for security and fraud prevention only)</p>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>How We Use Your Information</h3>
              <p style={bodyStyle}>We use your information to generate AI-powered insights, process payments, communicate with you about your purchase, any updates from enquips, and maintain platform security and performance. We do not sell, rent, or trade your personal information.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Sharing Your Information</h3>
              <p style={bodyStyle}>We share information only with trusted third-party providers who help us operate the platform — such as payment processors, hosting providers, and analytics tools. We do not share your data with advertisers.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Data Retention</h3>
              <p style={bodyStyle}>Journal entries are deleted immediately after insights are generated. Purchase and account information is retained as required for business, legal, or tax purposes. Analytics data is retained in aggregated, non-identifiable form only.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Your Rights</h3>
              <p style={bodyStyle}>You may request access to or deletion of your personal information at any time by contacting <a href="mailto:info@clarityjournalbyenquips.com" style={{ color: 'var(--accent-teal)', textDecoration: 'underline' }}>info@clarityjournalbyenquips.com</a>. We honor these requests whenever legally and operationally possible.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Children's Privacy</h3>
              <p style={bodyStyle}>Clarity Soundtrack is intended for adults 18 and older. We do not knowingly collect information from individuals under 18. If we learn a minor has used the Services, we will delete their information promptly.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Third-Party Links</h3>
              <p style={bodyStyle}>The Services may reference external resources for convenience. We are not responsible for the privacy practices of third-party websites.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Legal Basis for Processing</h3>
              <p style={bodyStyle}>We process your information based on your consent, our need to provide the services you request, and our legitimate interest in maintaining platform security and performance.</p>
            </div>

            {/* TERM OF SERVICE */}
            <div style={sectionStyle}>
              <h2 style={headingStyle}>Term of Service</h2>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>What Clarity Soundtrack Is</h3>
              <p style={bodyStyle}>Clarity Soundtrack provides guided prompts, reflective exercises, and meditative frameworks designed to support personal clarity and intentional living. The Services are for personal reflection only — not therapy, counseling, financial advice, legal advice, or medical guidance of any kind. No professional relationship is created between you and EnQUIPs LLC through your use of these Services.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Intellectual Property</h3>
              <div style={bodyStyle}>
                <p style={{ marginBottom: '8px' }}>All content within Clarity Soundtrack — including prompts, language, frameworks, audio, designs, branding, and digital materials — is the exclusive property of EnQUIPs LLC. Your purchase grants a personal, non-transferable, non-exclusive license for individual use only.</p>
                <p>You may not copy, reproduce, distribute, modify, or use the content for any commercial, coaching, consulting, or educational purpose. If you purchase Clarity Soundtrack as a gift, you may transfer this license to the intended recipient. Further redistribution is not permitted.</p>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Eligibility</h3>
              <p style={bodyStyle}>You must be at least 18 years old to purchase or use the Services.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Refund Policy</h3>
              <p style={bodyStyle}>This experience is delivered and made accessible instantly. Because the value cannot be returned or revoked, all sales are final.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>User Responsibilities</h3>
              <p style={bodyStyle}>You agree to use the Services lawfully, respectfully, and in a non-harmful manner. You may not use the Services to violate any laws, harm others, or attempt to access systems or data without authorization.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Limitation of Liability</h3>
              <p style={bodyStyle}>To the fullest extent permitted by law, EnQUIPs LLC is not liable for any indirect, incidental, or consequential damages — including loss of data, financial loss, or decisions you make based on the content. Governing law: State of Illinois.</p>
            </div>

            <div style={sectionStyle}>
              <h3 style={{ ...headingStyle, color: 'var(--text-primary)', opacity: 0.8 }}>Modifications</h3>
              <p style={bodyStyle}>We may update the Services or these Terms at any time. Continued use after changes constitutes acceptance of the updated Terms.</p>
            </div>

            {/* COOKIE POLICY */}
            <div style={{ marginBottom: '16px' }}>
              <h2 style={headingStyle}>Cookie Policy</h2>
              <p style={bodyStyle}>We use cookies to keep this page running and improve your experience.</p>
              <p style={{ ...bodyStyle, marginTop: '8px' }}>
                Simply click "noted" to acknowledge our cookie policy or{' '}
                <button
                  onClick={() => window.dispatchEvent(new Event('open-cookie-preferences'))}
                  style={{ 
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit',
                    color: 'var(--accent-teal)', opacity: 0.8, textDecoration: 'underline',
                    textUnderlineOffset: '3px', lineHeight: 'inherit'
                  }}
                >
                  Preferences
                </button>
              </p>
            </div>

          </div>

          {/* Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="cta-pill-landing"
              style={{ padding: '12px 32px', fontSize: '0.78rem' }}
              data-testid="accept-privacy-btn"
            >
              {loading ? 'Saving...' : "Noted, Let's Start the Session"}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="cta-pill-landing"
              style={{ padding: '12px 32px', fontSize: '0.78rem', opacity: 0.5 }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyNoticePage;
